import React, { useRef } from 'react';
import classNames from 'classnames';

import IconButton from '@zvuk-b2b/react-uikit/ui/IconButton';
import Dropdown, { DropdownItemDataType, DropdownPlacement } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import IconX from '@zvuk-b2b/react-uikit/ui/icons/X';
import Button, { ButtonSize, ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import IconSettingsSlider from '@zvuk-b2b/react-uikit/ui/icons/Settings copy';
import { Colors } from '@zvuk-b2b/react-uikit/ui/types';
import { InputSize } from '@zvuk-b2b/react-uikit/ui/Input';
import Spinner from '@zvuk-b2b/react-uikit/ui/Spinner';

import { TEXTS } from '../texts';

import styles from './index.module.scss';

type TagFiltersProps = {
  elementId?: string;
  buttonClassName?: string;
  onlyIcon: boolean;
  setIsExpanded: (value: boolean) => void;
  isExpanded: boolean;
  filterOptions: DropdownItemDataType[];
  onDropdownClose: () => void;
  onSearchChange: (value: string) => void;
  onSelect: (tag: DropdownItemDataType) => void;
  maxVisibleOptions: number;
  menuMargin?: number;
  isLoading: boolean;
  isDisabled?: boolean;
  shownTags: DropdownItemDataType[];
  placement?: DropdownPlacement;
};

const TagsDropdown = ({
  elementId,
  buttonClassName,
  setIsExpanded,
  isExpanded,
  filterOptions,
  onDropdownClose,
  onSearchChange,
  onSelect,
  maxVisibleOptions,
  menuMargin,
  isLoading,
  isDisabled = false,
  shownTags,
  onlyIcon,
  placement,
}: TagFiltersProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLInputElement>(null);

  const { isMobile } = useMedia();

  return (
    <div
      ref={wrapperRef}
      id={elementId}
      className={styles.TagsDropdownRoot}>
      <Button
        type={ButtonType.PLAIN}
        size={ButtonSize.SMALL}
        ref={anchorRef}
        isDisabled={filterOptions.length <= 0 || isDisabled}
        className={classNames(
          styles.TagsDropdownButton,
          buttonClassName,
          onlyIcon && styles.TagsDropdownButtonOnlyIcon
        )}
        adornmentLeftClassName={styles.TagsDropdownIconButton}
        elementId="button_expand_tags"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        adornmentLeft={
          isLoading && !onlyIcon ? (
            <Spinner
              className={styles.TagsDropdownSpinner}
              color={Colors.COLOR_NEUTRAL_500}
            />
          ) : (
            <div>
              {shownTags.length > 0 && <div className={styles.TagsDropdownIconButtonBadge} />}
              <IconSettingsSlider />
            </div>
          )
        }>
        {!onlyIcon && <div>{TEXTS.TAGS_FILTER_TITLE}</div>}
      </Button>

      {isExpanded && (
        <div className={styles.TagsDropdownInputWrapper}>
          {isMobile && (
            <IconButton
              elementId="tags_dropdown_close_button"
              className={styles.TagsDropdownCloseButton}
              onClick={(event) => {
                onDropdownClose();
                event.preventDefault();
              }}>
              <IconX />
            </IconButton>
          )}

          <Dropdown
            items={filterOptions}
            anchorNode={!isMobile ? wrapperRef.current! : anchorRef.current!}
            parentNode={wrapperRef.current!}
            elementId="tags_dropdown"
            onClose={onDropdownClose}
            onSearchChange={onSearchChange}
            isFullMode={isMobile}
            isMulti
            maxVisibleOptions={maxVisibleOptions}
            menuMargin={menuMargin}
            hasSearch
            onSelect={onSelect}
            withoutWrapper
            className={styles.TagsDropdown}
            searchInputSize={InputSize.SMALL}
            searchInputPlaceholder={TEXTS.TAGS_FILTER_SEARCH_TITLE}
            isNeedCloseWhileParentScrolling={false}
            placement={placement}
            inputContainerClassName={styles.inputContainer}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(TagsDropdown);
